import { io, Socket } from 'socket.io-client';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3000';

class SocketClient {
  private socket: Socket | null = null;
  private isConnecting = false;

  connect(token: string): Socket {
    if (this.socket?.connected) {
      return this.socket;
    }

    if (this.isConnecting) {
      return this.socket!;
    }

    this.isConnecting = true;

    try {
      this.socket = io(SOCKET_URL, {
        autoConnect: true,
        reconnection: true,
        // Tenta pra sempre, com espera crescendo até 10 s. Com 5 tentativas
        // (~17 s) o socket desistia durante qualquer deploy da API e o painel
        // ficava sem alerta nem som até alguém dar F5.
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        // Função, não objeto: cada reconexão manda o token ATUAL (depois de um
        // novo login o antigo já não vale e a API recusa o socket).
        auth: (cb) => cb({ token: localStorage.getItem('manager_token') || token }),
        transports: ['websocket', 'polling'],
      });

      this.socket.on('connect', () => {
        this.isConnecting = false;
      });

      this.socket.on('disconnect', (reason) => {
        console.warn('Socket disconnected:', reason);
        this.isConnecting = false;
      });

      this.socket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
        this.isConnecting = false;
      });

      return this.socket;
    } catch (error) {
      console.error('Failed to create socket connection:', error);
      this.isConnecting = false;
      throw error;
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnecting = false;
    }
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  emit(event: string, data?: unknown): void {
    if (!this.socket?.connected) {
      console.warn('Socket not connected. Cannot emit event:', event);
      return;
    }

    this.socket.emit(event, data);
  }

  on(event: string, callback: (...args: unknown[]) => void): void {
    if (!this.socket) {
      console.warn('Socket not initialized. Cannot listen to event:', event);
      return;
    }

    this.socket.on(event, callback);
  }

  off(event: string, callback?: (...args: unknown[]) => void): void {
    if (!this.socket) {
      return;
    }

    if (callback) {
      this.socket.off(event, callback);
    } else {
      this.socket.off(event);
    }
  }
}

export const socketClient = new SocketClient();

export const connectSocket = (token: string): Socket => {
  return socketClient.connect(token);
};

export const disconnectSocket = (): void => {
  socketClient.disconnect();
};

export const getSocket = (): Socket | null => {
  return socketClient.getSocket();
};

export const isSocketConnected = (): boolean => {
  return socketClient.isConnected();
};
